/**
 * @file SOCO membership level names (old and new) for the frontend.
 * Frontend copy of `sociolla-core/lib/transformers/soco-levels`: keep SOCO_LEVELS identical.
 *
 * SOCO membership levels were renamed (display and stored values; the level rules did not change):
 *
 *   | level code | old name (old slug)       | new name (new slug)           |
 *   |------------|---------------------------|-------------------------------|
 *   | member     | SOCO Star (soco-star)     | SOCO Member (soco-member)     |
 *   | silver     | Rising Star (rising-star) | SOCO Silver (soco-silver)     |
 *   | gold       | Super Star (super-star)   | SOCO Gold (soco-gold)         |
 *   | platinum   | Diva Star (diva-star)     | SOCO Platinum (soco-platinum) |
 *
 * API data can hold the old OR the new name/slug, so:
 * - show a level with `getLevelName(value, isNewUserLevelEnabled)`;
 * - compare levels with `getLevelCode`, `isLevel`, `isSameLevel`, `isLevelInList` or `getLevelRank`, never with
 *   `===`, regex or the first word of a name (all new names start with "SOCO");
 * - build CSS classes from `getOldLevelSlug` so existing styles keep working.
 *
 * `isNewUserLevelEnabled` comes from the host app env config (`NEW_USER_LEVEL_V2`).
 * `socoLevelMixin` (end of this file) reads `NEW_USER_LEVEL_V2` from config/default.env itself.
 * Lilla levels ('Basic', 'Silver', 'Gold', 'Platinum') are not SOCO levels; never pass them to these helpers.
 * @module soco-levels
 */

/** Stable code of each level. Use these in code instead of level names. */
export const LEVEL_CODES = Object.freeze({
  MEMBER: 'member',
  SILVER: 'silver',
  GOLD: 'gold',
  PLATINUM: 'platinum',
});

/** Highest level a member can reach. */
export const HIGHEST_LEVEL_CODE = LEVEL_CODES.PLATINUM;

/**
 * The four SOCO levels, lowest first. `settings_key` is the SocoXchange `level_settings` key (never renamed).
 */
export const SOCO_LEVELS = Object.freeze(
  [
    {
      code: LEVEL_CODES.MEMBER,
      rank: 0,
      old_name: 'SOCO Star',
      new_name: 'SOCO Member',
      old_slug: 'soco-star',
      new_slug: 'soco-member',
      settings_key: 'soco_star',
    },
    {
      code: LEVEL_CODES.SILVER,
      rank: 1,
      old_name: 'Rising Star',
      new_name: 'SOCO Silver',
      old_slug: 'rising-star',
      new_slug: 'soco-silver',
      settings_key: 'rising_star',
    },
    {
      code: LEVEL_CODES.GOLD,
      rank: 2,
      old_name: 'Super Star',
      new_name: 'SOCO Gold',
      old_slug: 'super-star',
      new_slug: 'soco-gold',
      settings_key: 'super_star',
    },
    {
      code: LEVEL_CODES.PLATINUM,
      rank: 3,
      old_name: 'Diva Star',
      new_name: 'SOCO Platinum',
      old_slug: 'diva-star',
      new_slug: 'soco-platinum',
      settings_key: 'diva_star',
    },
  ].map((level) => Object.freeze(level))
);

/** Every old and new level name. */
export const ALL_LEVEL_NAMES = Object.freeze(
  SOCO_LEVELS.reduce((names, level) => names.concat([level.old_name, level.new_name]), [])
);

/**
 * Lower-case, trim and collapse spaces, so 'SOCO STAR' and ' soco  star ' are the same text.
 * @param {string} text
 * @return {string}
 */
const toComparableText = (text) => text.trim().toLowerCase().replace(/\s+/g, ' ');

/** Every accepted way to write a level (name, slug, settings key) to its level. */
const LEVEL_BY_TEXT = SOCO_LEVELS.reduce((lookup, level) => {
  [level.old_name, level.new_name, level.old_slug, level.new_slug, level.settings_key].forEach((text) =>
    lookup.set(toComparableText(text), level)
  );
  return lookup;
}, new Map());

/** A level by its code ('member', 'silver', 'gold', 'platinum'); exact match only. */
const LEVEL_BY_CODE = new Map(SOCO_LEVELS.map((level) => [level.code, level]));

/**
 * Find a SOCO level from any way it is written: old or new name, old or new slug (any letter case), settings key,
 * or level code.
 * @param {string} value
 * @return {object|null} the level from SOCO_LEVELS, or null when the value is not a SOCO level
 */
export function findLevel(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  return LEVEL_BY_CODE.get(value) || LEVEL_BY_TEXT.get(toComparableText(value)) || null;
}

/**
 * @param {string} value
 * @return {string|null} level code ('member', 'silver', 'gold', 'platinum') or null
 */
export function getLevelCode(value) {
  const level = findLevel(value);
  return level ? level.code : null;
}

/**
 * @param {string} value
 * @param {string} levelCode one of LEVEL_CODES
 * @return {boolean}
 */
export function isLevel(value, levelCode) {
  return getLevelCode(value) === levelCode;
}

/**
 * @param {string} firstValue
 * @param {string} secondValue
 * @return {boolean} true when both are the same SOCO level, e.g. 'Rising Star' and 'SOCO Silver', or the exact same text
 */
export function isSameLevel(firstValue, secondValue) {
  if (typeof firstValue === 'string' && firstValue === secondValue) {
    return true;
  }
  const firstCode = getLevelCode(firstValue);
  return firstCode !== null && firstCode === getLevelCode(secondValue);
}

/**
 * Replacement for `list.includes(value)` when the list holds level names.
 * @param {Array<string>} list
 * @param {string} value
 * @return {boolean}
 */
export function isLevelInList(list, value) {
  return Array.isArray(list) && list.some((item) => isSameLevel(item, value));
}

/**
 * @param {string} value
 * @return {number} 0 (member) to 3 (platinum), or -1 when the value is not a SOCO level
 */
export function getLevelRank(value) {
  const level = findLevel(value);
  return level ? level.rank : -1;
}

/**
 * @param {string} value
 * @return {Array<string>} [old name, new name]; a value that is not a SOCO level is returned as [value]
 */
export function getAllNamesOfLevel(value) {
  const level = findLevel(value);
  return level ? [level.old_name, level.new_name] : [value];
}

/**
 * Name to show for a level.
 * @param {string} value stored level (name or slug, old or new) or level code
 * @param {boolean} isNewUserLevelEnabled NEW_USER_LEVEL_V2 of the host app
 * @return {string} new name when enabled, old name otherwise; a value that is not a SOCO level is returned as is
 */
export function getLevelName(value, isNewUserLevelEnabled) {
  const level = findLevel(value);
  if (!level) {
    return value || '';
  }
  return isNewUserLevelEnabled === true ? level.new_name : level.old_name;
}

/**
 * Slug for level image file names: new slug when enabled, old slug otherwise.
 * @param {string} value
 * @param {boolean} isNewUserLevelEnabled
 * @param {string} defaultSlug slug used when the value is not a SOCO level
 * @return {string}
 */
export function getLevelImageSlug(value, isNewUserLevelEnabled, defaultSlug = 'soco-star') {
  const level = findLevel(value);
  if (!level) {
    return defaultSlug;
  }
  return isNewUserLevelEnabled === true ? level.new_slug : level.old_slug;
}

/**
 * Old slug of a level, for existing CSS classes and as the fallback image when a new image is missing.
 * @param {string} value
 * @return {string|null}
 */
export function getOldLevelSlug(value) {
  const level = findLevel(value);
  return level ? level.old_slug : null;
}

// ---------------------------------------------------------------- Vue mixin

const Config = require('~/config/default.env').default;

const IS_NEW_USER_LEVEL_ENABLED = String(Config.NEW_USER_LEVEL_V2) === 'true';

/**
 * Vue mixin for the SOCO level rename: level name, CSS class and image (with old-slug fallback).
 */
export const socoLevelMixin = {
  methods: {
    getSocoLevelName(value) {
      return getLevelName(value, IS_NEW_USER_LEVEL_ENABLED);
    },
    getSocoLevelClass(value) {
      return getOldLevelSlug(value) || 'soco-star';
    },
    getSocoLevelImage(value, folder = '/assets/img', suffix = '') {
      return `${folder}/${getLevelImageSlug(value, IS_NEW_USER_LEVEL_ENABLED)}${suffix}.svg`;
    },
    // the new image files may not exist yet: fall back to the old slug file
    getSocoLevelFallbackImage(imageSrc) {
      return SOCO_LEVELS.reduce((src, level) => src.replace(`/${level.new_slug}`, `/${level.old_slug}`), imageSrc);
    },
    onSocoLevelImageError(event) {
      const image = event.target;
      if (image.dataset.socoLevelFallback) {
        return;
      }
      image.dataset.socoLevelFallback = 'true';
      image.src = this.getSocoLevelFallbackImage(image.getAttribute('src'));
    },
  },
};
